import qs from 'qs'
import { UseMutationOptions, UseQueryOptions, useMutation, useQuery } from '@tanstack/react-query'
import { zip } from 'lodash'
import { queryClient } from '~/common/react-query/queryClient'
import { REPORTS_QUERY_KEY, customReportsApi } from '~/api/reports'
import fetch from '~/common/fetch'
import { mapToSavedReport } from '~/custom_reports/sharedV3'
import {
  BaseCustomReportQueryKeyParams,
  CustomReportQueryKeyParams,
  Dataset,
  DeleteReportPayload,
  DatasetRelation,
  FieldTypes,
  FilterOptionsResponse,
  Join,
  JoinOption,
  ListAllRelatedFieldsResponse,
  ListReportScheduleResponse,
  ListSftpOptionsResponse,
  ListSystemVariablesResponse,
  QueryFilter,
  Report,
  ReportDetails,
  ReportDetailsPayload,
  ReportQuery,
  ExecuteReportQueryResponse,
  ReportScheduleResponse,
  RunFormattingPreviewPayload,
  RunFormattingPreviewResponse,
  SavedReport,
  SaveReportPayload,
  SaveSchedulePayload,
  UpdateReportResponse,
  ReportFieldsPayload,
  RevertReportPayload,
  VersionType,
  ReportQueryInfoResponse,
  ReportQueryInfoKeyParams,
  ReportQueryRequest,
  ReportQueryInfoRequest,
  ReportQueryResponse,
  AnalyzeReportQueryResponse,
  ReportFileItem
} from '~/custom_reports/types'
import { OffsetLimit, OffsetLimitPaginationResponse } from '~/api/common'
import { arrayToObject } from '~/common/arrayUtils'
import { SCHEDULED_JOB_TYPE } from '~/api/scheduled_jobs/types'

// Owned here (not in ~/custom_reports/api) so that module doesn't need to import back from this
// one — ~/custom_reports/api already imports reportsV3Api from here, and a reportsV3.ts -> that
// module -> back to reportsV3Api require cycle non-deterministically left customReportsApi missing
// its reportsV3Api-derived methods (e.g. `.reports.get`), depending on which module loaded first.
export const SEARCH_ALL_RELATED_FIELDS = 'reporting-search-all-related-fields-{search}-{fieldType}'

const QUERY_STALE_TIME = 1000 * 60 * 5 // Five Minutes
export const reportsV3Api = {
  formats: {
    runFormattingPreview: (payload: RunFormattingPreviewPayload): Promise<RunFormattingPreviewResponse> =>
      fetch.post('/api/custom_reports/preview_field_format/', payload)
  },
  reports: {
    save: (reportId: number, report: Report): Promise<unknown> =>
      fetch.post(`/api/custom_reports/saved_report/${reportId}`, report),
    listSystemVariables: (): Promise<ListSystemVariablesResponse> => fetch.get('/api/custom_reports/system_variables'),
    get: (id: number, version: number | null): Promise<SavedReport> => {
      const urlParams = version ? { version } : {}
      const url = `/api/custom_reports/saved_report/${id}/?${qs.stringify(urlParams)}`
      return fetch.get(url)
    },
    executeQuery: (
      { id, report_type, query, ...urlParams }: ReportQueryRequest,
      options: { signal?: AbortSignal } = {}
    ): Promise<ExecuteReportQueryResponse> =>
      fetch.post(
        `/api/custom_reports/query/execute/?${qs.stringify(urlParams)}`,
        { id, report_type, ...query },
        options
      ),
    generateReportQueryInfo: (
      { id, report_type, query, limit, ...urlParams }: ReportQueryInfoRequest,
      options: { signal?: AbortSignal } = {}
    ): Promise<ReportQueryInfoResponse> =>
      fetch.post(
        `/api/custom_reports/query/info/?${qs.stringify(urlParams)}`,
        { id, report_type, ...query, limit },
        options
      ),
    analyzeQuery: (
      { id, report_type, query, ...urlParams }: ReportQueryRequest,
      options: { signal?: AbortSignal } = {}
    ): Promise<AnalyzeReportQueryResponse> =>
      fetch.post(
        `/api/custom_reports/query/analyze/?${qs.stringify(urlParams)}`,
        { id, report_type, ...query },
        options
      )
  },
  reportDetails: {
    save: (reportId: number, reportDetails: ReportDetails): Promise<unknown> =>
      fetch.post(`/api/custom_reports/v3/${reportId}`, reportDetails)
  },
  datasets: {
    list: (): Promise<Dataset[]> => fetch.get(`/api/custom_reports/datasets`),
    getRelations: (datasetId: string): Promise<DatasetRelation[]> =>
      fetch.get(`/api/custom_reports/${datasetId}/relations`),
    listAllRelatedFields: (
      reportQuery: Partial<ReportQuery>,
      options: { signal?: AbortSignal } = {}
    ): Promise<ListAllRelatedFieldsResponse> =>
      fetch.post(`/api/custom_reports/query/fields/?field_type=ALL`, { query: reportQuery }, options),
    searchRelatedFields: (
      reportQuery: Partial<ReportQuery>,
      paths?: string[],
      search?: string,
      field_type?: FieldTypes,
      options: { signal?: AbortSignal } = {}
    ): Promise<ListAllRelatedFieldsResponse> =>
      fetch.post(
        `/api/custom_reports/query/fields/?${qs.stringify({ search, field_type })}`,
        { query: reportQuery, paths: paths ?? null },
        options
      ),
    getFilterOptions: (datasetId: string, fieldId: string, omittedIds: number[] = []): Promise<FilterOptionsResponse> =>
      fetch.get(`/api/custom_reports/filter_options/${datasetId}/${fieldId}?&omittedIds=${omittedIds}`)
  },
  schedules: {
    listSftpDefinitions: (): Promise<ListSftpOptionsResponse> => fetch.get(`/api/custom_reports/sftp_options/`),
    list: (reportId: string): Promise<ListReportScheduleResponse> =>
      fetch.get(`/api/scheduled_jobs/?type=${SCHEDULED_JOB_TYPE.CUSTOM_REPORT}&reference_id=${reportId}`),
    get: (reportId: string, scheduleId: number): Promise<ReportScheduleResponse | null> => {
      const resp = fetch.get(
        `/api/scheduled_jobs/?type=${SCHEDULED_JOB_TYPE.CUSTOM_REPORT}&reference_id=${reportId}&id=${scheduleId}`
      ) as Promise<ReportScheduleResponse[]>
      return resp.then(values => (values.length > 0 ? values[0] : null))
    },
    create: (request: SaveSchedulePayload) => fetch.post('/api/scheduled_jobs/', request),
    delete: (scheduleId: number) =>
      fetch.delete(`/api/scheduled_jobs/${scheduleId}/?type=${SCHEDULED_JOB_TYPE.CUSTOM_REPORT}`),
    update: (request: SaveSchedulePayload) =>
      fetch.put(`/api/scheduled_jobs/${request.id}/?type=${SCHEDULED_JOB_TYPE.CUSTOM_REPORT}`, request),
    execute: (scheduleId: number) => fetch.post(`/api/custom_reports/scheduled_reports/${scheduleId}/run`),
    getActivityLogs: (scheduleId: number, queryParams?: Partial<OffsetLimit>) =>
      fetch.get(
        `/api/scheduled_jobs/${scheduleId}/activity-log?type=${SCHEDULED_JOB_TYPE.CUSTOM_REPORT}&${qs.stringify(
          queryParams
        )}`
      )
  }
}

export const REPORT_BY_ID = 'report/{id}'
export const REPORT_BY_ID_AND_VERSION = 'report/{id}/{version}'

const invalidateReportByIdQueries = async (reportId: number) => {
  await Promise.all([
    queryClient.invalidateQueries([REPORT_BY_ID, { id: reportId }]),
    queryClient.invalidateQueries([REPORT_BY_ID_AND_VERSION, { id: reportId }]),
    queryClient.invalidateQueries([SAVED_REPORT_VERSION_QUERY_KEY, { savedReportId: reportId }]),
    queryClient.invalidateQueries([CUSTOM_REPORTS_QUERY, { id: reportId }]),
    queryClient.invalidateQueries([CUSTOM_REPORTS_INFO_QUERY, { id: reportId }]),
    queryClient.invalidateQueries([ANALYZE_CUSTOM_REPORTS_QUERY, { id: reportId }])
  ])
}

const DATASET_RELATIONS_KEY = 'relations/{id}/'
export const CUSTOM_REPORTS_QUERY = 'custom-report-query-v3'
export const ANALYZE_CUSTOM_REPORTS_QUERY = 'analyze-custom-report-query'
export const CUSTOM_REPORTS_INFO_QUERY = 'custom-report-query-info'

export const useGetDatasetRelations = (
  { dataset, joins }: { dataset: string; joins: Join[] },
  config?: UseQueryOptions<JoinOption[]>
) => {
  return useQuery({
    queryKey: [DATASET_RELATIONS_KEY, { dataset, joins }],
    queryFn: () => customReportsApi.getDatasetRelations(dataset, joins) as Promise<JoinOption[]>,
    ...config
  })
}

export const useGetReportQuery_DEPR = ({ id }: { id: number }, config?: UseQueryOptions<SavedReport>) =>
  useQuery({ queryKey: [REPORT_BY_ID, { id }], queryFn: () => customReportsApi.getSavedReport(id), ...config })

export const useGetReportQuery = (
  { id, versionId }: { id: number; versionId: number | null },
  config?: UseQueryOptions<SavedReport>
) =>
  useQuery({
    queryKey: [REPORT_BY_ID_AND_VERSION, { id, versionId }],
    queryFn: () => reportsV3Api.reports.get(id, versionId),
    ...config
  })

export const buildQuery = (query: ReportQuery, viewFilters: QueryFilter[] | null) => ({
  ...query,
  viewFilters: viewFilters || []
})

export const useAnalyzeCustomReportQuery = (
  query: ReportQuery,
  {
    id,
    report_type,
    formats,
    aggregations,
    queryParams,
    offset,
    limit,
    orderBy,
    viewFilters
  }: BaseCustomReportQueryKeyParams,
  config?: UseQueryOptions<AnalyzeReportQueryResponse>
) => {
  const queryKey = [
    ANALYZE_CUSTOM_REPORTS_QUERY,
    {
      id,
      formats: formats ?? null,
      aggregations: aggregations ?? null,
      report_type,
      queryParams: {
        dataset: queryParams.dataset,
        filters: queryParams.filters,
        orderBy: queryParams.orderBy,
        parameterFields: queryParams.parameterFields,
        calculatedFields: queryParams.calculatedFields
      },
      offset,
      limit,
      orderBy,
      viewFilters
    }
  ]

  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await reportsV3Api.reports.analyzeQuery(
        {
          id: id ?? undefined,
          report_type,
          offset,
          limit,
          query: { ...buildQuery(query, viewFilters), orderBy }
        },
        { signal }
      )
      return response
    },
    ...config,
    enabled: !!query?.dataset && !!query?.fields.length,
    keepPreviousData: true,
    staleTime: QUERY_STALE_TIME
  })
}

export const useRunCustomReportQuery = (
  query: ReportQuery,
  {
    id,
    report_type,
    formats,
    aggregations,
    queryParams,
    offset,
    limit,
    orderBy,
    viewFilters,
    versionId
  }: CustomReportQueryKeyParams,
  isRunDisabled: boolean,
  config?: UseQueryOptions<ExecuteReportQueryResponse, unknown, ReportQueryResponse>
) => {
  const queryKey = [
    CUSTOM_REPORTS_QUERY,
    {
      id,
      formats: formats ?? null,
      aggregations: aggregations ?? null,
      report_type,
      queryParams: {
        dataset: queryParams.dataset,
        filters: queryParams.filters,
        orderBy: queryParams.orderBy,
        parameterFields: queryParams.parameterFields,
        calculatedFields: queryParams.calculatedFields
      },
      offset,
      limit,
      orderBy,
      viewFilters,
      versionId
    }
  ]
  return useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      const response = await reportsV3Api.reports.executeQuery(
        {
          id: id ?? undefined,
          report_type,
          offset,
          limit,
          query: { ...buildQuery(query, viewFilters), orderBy },
          col_format: 'ui',
          versionId: versionId ?? undefined
        },
        { signal }
      )
      return {
        ...response,
        data:
          response.data.map(row =>
            arrayToObject(
              zip(query.fields, row),
              ([column]) => column?.id as string,
              ([_, value]) => value
            )
          ) ?? []
      }
    },
    ...config,
    enabled: !!query.dataset && !!query.fields.length && !isRunDisabled,
    keepPreviousData: true,
    staleTime: QUERY_STALE_TIME
  })
}

export const useGenerateReportQueryInfoQuery = (
  query: ReportQuery,
  { id, report_type, formats, queryParams, orderBy, viewFilters, versionId, limit }: ReportQueryInfoKeyParams,
  isRunDisabled: boolean,
  config?: UseQueryOptions<ReportQueryInfoResponse>
) =>
  useQuery({
    queryKey: [
      CUSTOM_REPORTS_INFO_QUERY,
      {
        id,
        formats,
        report_type,
        queryParams,
        viewFilters,
        versionId,
        limit
      }
    ],
    queryFn: async ({ signal }) =>
      reportsV3Api.reports.generateReportQueryInfo(
        {
          id: id ?? undefined,
          report_type,
          query: { ...buildQuery(query, viewFilters), orderBy },
          col_format: 'ui',
          versionId: versionId ?? undefined,
          limit
        },
        { signal }
      ),
    ...config,
    enabled: !!query.dataset && !!query.fields.length && !isRunDisabled,
    keepPreviousData: true,
    staleTime: QUERY_STALE_TIME
  })

export const useReportDetailsMutation = (
  opts: UseMutationOptions<UpdateReportResponse, unknown, ReportDetailsPayload>
) =>
  useMutation({
    mutationFn: (payload: ReportDetailsPayload) => {
      const mapped = mapToSavedReport(payload)
      return mapped.id === null || mapped.id === undefined
        ? customReportsApi.saveReport(mapped)
        : customReportsApi.updateReport(mapped)
    },
    ...opts,
    onSuccess: async (data, variables, context) => {
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useReportRevertMutation = (opts: UseMutationOptions<unknown, unknown, RevertReportPayload>) =>
  useMutation({
    mutationFn: (payload: RevertReportPayload) =>
      customReportsApi.revertReport(payload.reportId, { version: payload.selectedVersion }),
    ...opts,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries([REPORT_BY_ID])
      await queryClient.invalidateQueries([REPORT_BY_ID_AND_VERSION])
      await queryClient.invalidateQueries([SAVED_REPORT_VERSION_QUERY_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useReportUpdateMutation = (opts: UseMutationOptions<unknown, unknown, ReportFieldsPayload>) =>
  useMutation({
    mutationFn: (payload: ReportFieldsPayload) => {
      const mapped = mapToSavedReport(payload)
      return customReportsApi.updateReport(mapped)
    },
    ...opts,
    onSuccess: async (data, variables, context) => {
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useSaveReportMutation = (opts: UseMutationOptions<UpdateReportResponse, unknown, SaveReportPayload>) =>
  useMutation({
    mutationFn: (payload: SaveReportPayload) => {
      const mapped = mapToSavedReport(payload)
      if (mapped.id === null || mapped.id === undefined) {
        return customReportsApi.saveReport(mapped)
      }
      return customReportsApi.updateReport(mapped)
    },
    ...opts,
    onSuccess: async (data, variables, context) => {
      const reportId = variables.id
      if (reportId != null) {
        await invalidateReportByIdQueries(reportId)
      }
      await queryClient.invalidateQueries([REPORTS_QUERY_KEY])
      await queryClient.invalidateQueries([SEARCH_ALL_RELATED_FIELDS])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useSaveNewReportMutation = (opts: UseMutationOptions<UpdateReportResponse, unknown, SaveReportPayload>) =>
  useMutation({
    mutationFn: (paylod: SaveReportPayload) => {
      const mapped = mapToSavedReport(paylod)
      return customReportsApi.saveReport(mapped)
    },
    ...opts,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries([REPORTS_QUERY_KEY])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const useDeleteSavedReportMutation = (opts: UseMutationOptions<unknown, unknown, DeleteReportPayload>) =>
  useMutation({
    mutationFn: ({ reportId }: DeleteReportPayload) => {
      return customReportsApi.deleteReport(reportId)
    },
    ...opts,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries([REPORTS_QUERY_KEY])
      await queryClient.invalidateQueries([REPORT_BY_ID, { id: variables.reportId }])
      if (typeof opts?.onSuccess === 'function') opts.onSuccess(data, variables, context)
    }
  })

export const SAVED_REPORT_VERSION_QUERY_KEY = 'saved_report_version_history'
export const useSavedReportVersionHistory = (
  savedReportId: number,
  queryParams = {},
  config?: UseQueryOptions<OffsetLimitPaginationResponse<VersionType>>
) =>
  useQuery({
    queryKey: [SAVED_REPORT_VERSION_QUERY_KEY, { savedReportId, queryParams }],
    queryFn: () => customReportsApi.getSavedReportVersionHistory(savedReportId, queryParams),
    ...config
  })

// Report File History API
const REPORT_FILE_LIST_QUERY = 'report-file-list'

type ReportFileListQueryParams = {
  offset?: number
  limit?: number
  search?: string
  ordering?: string
  export_type?: string | string[]
  file_type?: string | string[]
  created_at?: { from?: string; to?: string }
  id?: number
}

export const reportFileListQueryOpts = (queryParams: ReportFileListQueryParams) => ({
  queryKey: [REPORT_FILE_LIST_QUERY, queryParams],
  queryFn: (): OffsetLimitPaginationResponse<ReportFileItem> =>
    fetch.get(`/api/custom_reports/report_file/?${qs.stringify(queryParams, { arrayFormat: 'comma' })}`)
})

const REPORT_SPECIFIC_FILE_LIST_QUERY = 'report-specific-file-list'

export const reportSpecificFileListQueryOpts = (reportId: number | null, queryParams: ReportFileListQueryParams) => ({
  queryKey: [REPORT_SPECIFIC_FILE_LIST_QUERY, reportId, queryParams],
  queryFn: () =>
    fetch.get(
      `/api/custom_reports/report_file/?saved_report_id=${reportId}&${qs.stringify(queryParams, {
        arrayFormat: 'comma'
      })}`
    ),
  enabled: reportId !== null && reportId !== undefined
})

export const useGetReportFilesByReportIdQuery = (reportId: number | null) =>
  useQuery({
    ...reportSpecificFileListQueryOpts(reportId, { ordering: '-created_at', offset: 0, limit: 30 }),
    staleTime: QUERY_STALE_TIME
  })
